import {
  ActionError,
  AttentionBlock,
  Button,
  ListRow,
  ListRows,
  Skeleton,
  formatCurrency,
  plural,
  toast,
} from '@westhetics/ui'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { LoadError } from '@/app/load-error'
import {
  useProcedures,
  useSaveProcedure,
  type PaymentTerms,
  type Procedure,
} from '@/features/procedures/api'
import {
  emptyProcedureForm,
  procedureInput,
  procedureServerErrors,
  validateProcedureForm,
  type ProcedureFormErrors,
  type ProcedureFormState,
} from '@/features/procedures/procedure-form-state'
import { ProcedureFormBlocks } from '@/features/procedures/procedure-form'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import type { Onboarding } from './api'

/**
 * A etapa Procedimentos da implantação (docs/areas/administracao.md, "Implantação da clínica";
 * RN-ADM-83, T47). Mostra o que a clínica já cadastrou e, até a pessoa concluir a implantação,
 * sempre deixa adicionar mais um: o formulário do procedimento abre aqui mesmo, sem sair da etapa.
 * O procedimento é a oferta da clínica (nome, valor, condição) ligada a um ou mais procedimentos da
 * lista da Westhetics. A clínica do legado vê também os procedimentos sem vínculo.
 */

const FROM_ONBOARDING = '?from=onboarding'

export function OnboardingProcedures({
  summary,
  isAdding,
  onAddingChange,
  paymentTerms,
  onAdded,
}: {
  summary: Onboarding['summary']
  /** O formulário do procedimento novo está aberto. */
  isAdding: boolean
  onAddingChange: (isAdding: boolean) => void
  /** A condição de pagamento do último procedimento adicionado: o próximo já nasce com ela. */
  paymentTerms: PaymentTerms | null
  onAdded: (paymentTerms: PaymentTerms) => void
}) {
  // A API entrega até 50 por página; a clínica com mais vê o resto em Procedimentos.
  const list = useProcedures({ filter: 'all', perPage: 50 })
  // Cada procedimento adicionado troca a chave e o formulário recomeça vazio.
  const [composerKey, setComposerKey] = useState(0)
  const pendingLinks = summary.pendingCatalogLinkCount
  const procedures = list.data?.data ?? []
  const hidden = Math.max(0, (list.data?.meta.total ?? 0) - procedures.length)
  const isEmpty = list.isSuccess && procedures.length === 0
  // Sem nenhum procedimento, o formulário já abre: é o que a etapa pede.
  const showComposer = isAdding || isEmpty

  return (
    <>
      {pendingLinks > 0 ? (
        <AttentionBlock
          title={
            pendingLinks === 1
              ? '1 procedimento ainda sem vínculo com a lista da Westhetics'
              : `${pendingLinks} procedimentos ainda sem vínculo com a lista da Westhetics`
          }
        >
          <p>
            Vieram do sistema antigo. Vincule cada um a procedimentos da lista, ou arquive o que a
            clínica não vende mais: só então a implantação avança.
          </p>
        </AttentionBlock>
      ) : null}

      {list.isError ? (
        <LoadError
          title="Não conseguimos carregar os procedimentos da clínica"
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <div role="status" aria-label="Carregando" className="flex flex-col gap-2">
          <Skeleton className="h-[52px] w-full" />
          <Skeleton className="h-[52px] w-full" />
        </div>
      ) : procedures.length > 0 ? (
        <section aria-labelledby="onboarding-procedures-title" className="flex flex-col gap-3">
          <div>
            <h2 id="onboarding-procedures-title" className="eyebrow">
              No catálogo da clínica
            </h2>
            <p className="font-body text-secondary mt-[6px] text-[12px] leading-[1.6] font-light">
              {plural(summary.procedureCount, 'procedimento')} ligado
              {summary.procedureCount === 1 ? '' : 's'} à lista da Westhetics ·{' '}
              {plural(summary.consentFormCount, 'termo')} de consentimento chegam com eles.
            </p>
          </div>
          <ListRows>
            {procedures.map((procedure) => (
              <ProcedureRow key={procedure.id} procedure={procedure} />
            ))}
          </ListRows>
          {hidden > 0 ? (
            <p className="font-body text-secondary text-[12px] leading-[1.6] font-light">
              E mais {plural(hidden, 'procedimento')} em{' '}
              <Button variant="text" className="text-[12px]" asChild>
                <Link to={routes.admin.procedures}>Procedimentos</Link>
              </Button>
              .
            </p>
          ) : null}
        </section>
      ) : null}

      {showComposer ? (
        <ProcedureComposer
          key={composerKey}
          paymentTerms={paymentTerms}
          canCancel={!isEmpty}
          onCancel={() => onAddingChange(false)}
          onAdded={(terms) => {
            setComposerKey((key) => key + 1)
            onAddingChange(false)
            onAdded(terms)
          }}
        />
      ) : null}

      {list.isSuccess ? (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {showComposer ? null : (
            <Button
              className="max-desk:min-h-touch"
              variant="outline"
              onClick={() => onAddingChange(true)}
            >
              + Adicionar mais um procedimento
            </Button>
          )}
          <Button variant="text" asChild>
            <Link to={`${routes.admin.addProcedures}${FROM_ONBOARDING}`}>
              escolher vários da lista de uma vez
            </Link>
          </Button>
        </div>
      ) : null}
    </>
  )
}

/** "Toxina botulínica × 2 + Bioestimulador de colágeno": o que o procedimento inclui. */
function itemsLine(procedure: Procedure) {
  return procedure.items
    .map((item) =>
      item.quantity > 1
        ? `${item.catalogProcedure.name} × ${item.quantity}`
        : item.catalogProcedure.name,
    )
    .join(' + ')
}

function ProcedureRow({ procedure }: { procedure: Procedure }) {
  const href = `${routes.admin.procedure(procedure.id)}${FROM_ONBOARDING}`
  if (procedure.isPendingCatalogLink) {
    return (
      <ListRow
        critical
        title={procedure.name}
        meta={<span className="text-plum">Sem vínculo com a lista da Westhetics</span>}
        trailing={
          <Button variant="text" asChild>
            <Link to={href}>vincular</Link>
          </Button>
        }
      />
    )
  }
  return (
    <ListRow
      title={procedure.name}
      meta={`${itemsLine(procedure)} · ${formatCurrency(procedure.amountCents / 100)}`}
      trailing={
        <Button variant="text" asChild>
          <Link to={href} aria-label={`Editar ${procedure.name}`}>
            editar
          </Link>
        </Button>
      }
    />
  )
}

/** O formulário de um procedimento novo, dentro da etapa. */
function ProcedureComposer({
  paymentTerms,
  canCancel,
  onCancel,
  onAdded,
}: {
  paymentTerms: PaymentTerms | null
  canCancel: boolean
  onCancel: () => void
  onAdded: (paymentTerms: PaymentTerms) => void
}) {
  const save = useSaveProcedure()
  const [form, setForm] = useState<ProcedureFormState>(() => emptyProcedureForm(paymentTerms))
  const [errors, setErrors] = useState<ProcedureFormErrors>({})
  const [submitted, setSubmitted] = useState(false)

  function change(next: ProcedureFormState) {
    setForm(next)
    if (submitted) setErrors(validateProcedureForm(next))
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitted(true)
    const found = validateProcedureForm(form)
    setErrors(found)
    if (Object.keys(found).length > 0) return
    save.mutate(procedureInput(form), {
      onSuccess: () => {
        toast(`"${form.name.trim()}" adicionado ao catálogo`)
        onAdded(form.paymentTerms)
      },
      onError: (error) => setErrors(procedureServerErrors(error)),
    })
  }

  const hasFieldError = save.isError && Object.keys(procedureServerErrors(save.error)).length > 0
  const conflict =
    save.error && isApiError(save.error) && save.error.code === 'conflict'
      ? (save.error.details.existing as { id: string; isArchived: boolean } | undefined)
      : undefined

  return (
    <form
      onSubmit={submit}
      noValidate
      aria-label="Novo procedimento"
      className="flex flex-col gap-4"
    >
      <div>
        <h2 className="eyebrow">Novo procedimento</h2>
        <p className="font-body text-secondary mt-[6px] text-[12px] leading-[1.6] font-light">
          Dê o nome e o valor que a clínica usa e ligue aos procedimentos da lista da Westhetics que
          ele inclui.
        </p>
      </div>
      {save.isError && !hasFieldError ? (
        <ActionError
          title="Não foi possível adicionar o procedimento"
          description={errorMessage(save.error)}
          actions={
            conflict ? (
              <Button variant="outline" size="sm" asChild>
                <Link to={`${routes.admin.procedure(conflict.id)}${FROM_ONBOARDING}`}>
                  {conflict.isArchived ? 'Ver o arquivado' : 'Abrir o existente'}
                </Link>
              </Button>
            ) : undefined
          }
        />
      ) : null}
      <ProcedureFormBlocks form={form} errors={errors} onChange={change} />
      <div className="flex flex-wrap justify-end gap-3">
        {canCancel ? (
          <Button className="max-desk:min-h-touch" variant="text" onClick={onCancel}>
            Cancelar
          </Button>
        ) : null}
        <Button
          className="max-desk:min-h-touch"
          variant="outline"
          type="submit"
          loading={save.isPending}
          loadingLabel="Adicionando ao catálogo…"
        >
          Adicionar procedimento
        </Button>
      </div>
    </form>
  )
}
